import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/auth/AuthProvider";
import { TextField } from "@/components/TextField";
import { PushToAccountToggle } from "@/components/PushToAccountToggle";
import {
  getBondGia,
  giaBasisLabel,
  giaIsEditable,
  giaServerErrors,
  giaStatusLabel,
  readGiaStatus,
  saveGiaSigners,
  sendBondGia,
  validateGiaEmails,
  type BondGia,
  type BondGiaSummary,
  type GiaStatus,
} from "@/lib/brokerGia";
import { statusBadgeClass } from "@/lib/listFormat";
import { formatBondDate } from "@/lib/bondValueDisplay";
import { messageFor } from "@/lib/errors";

// BRK-S182 — the GIA section. ONE component, rendered in two places: the
// Request submitted screen (approved outcome only) and the submission profile.
// The confirmation is transient, so the reminder (PMS-S486) deep-links to the
// profile with `?focus=gia` — see `showsGiaSection` for where it renders at
// all, which is the caller's decision.
//
// ── NO OPTIMISTIC SUCCESS (the BRK-S122 class) ───────────────────────────────
//
// The chip is drawn from the SERVER's read and nothing else. "Send for
// signature" saves the emails, then sends, and the section moves to Sent only
// when the send answers 2xx with a read whose status is `sent`. A refusal, a
// network failure or a 2xx that does not say `sent` keeps the button live and
// says why. There is no local "sent" flag to forget to roll back.
//
// ── ERRORS LIVE ON THE ROW THEY ARE ABOUT (BRK-S181 revision 4) ──────────────
//
// A missing, malformed or shared email is about a PERSON, so it renders inside
// that person's row — the client's check and the server's 400 alike. Only a
// refusal that names nobody (a 409, a network failure) sits beside the button.

/** The `?focus=` value that means "the GIA section" — half of an agreement
 *  with PMS's reminder template, so a constant rather than an inline string. */
export const GIA_FOCUS = "gia";

/** The chip palette, borrowed from the shared list badges so a signed GIA
 *  reads the same green as any other finished thing. */
function chipClass(status: GiaStatus | null): string {
  switch (status) {
    case "signed":
      return statusBadgeClass("approved");
    case "sent":
      return statusBadgeClass("submitted");
    case "declined":
      return statusBadgeClass("declined");
    default:
      return statusBadgeClass("draft");
  }
}

export function GiaSection({
  orderId,
  summary,
  focus = false,
}: {
  orderId: string;
  /** The order read's `gia` block — the chip until the section's own read
   *  lands. */
  summary: BondGiaSummary;
  /** Scroll to the section and focus it once loaded (`?focus=gia`). */
  focus?: boolean;
}) {
  const { authedRequest } = useAuth();
  const [gia, setGia] = useState<BondGia | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [emails, setEmails] = useState<Record<string, string>>({});
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [attempt, setAttempt] = useState(0);
  // The wizard's choice, asked the same way and defaulting the same way: ON.
  const [pushToAccount, setPushToAccount] = useState(true);

  const section = useRef<HTMLElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const focused = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    getBondGia(authedRequest, orderId)
      .then((read) => {
        if (cancelled) return;
        setGia(read);
        setEmails(
          Object.fromEntries(
            read.signers.map((s) => [s.ownerId, s.email ?? ""]),
          ),
        );
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(messageFor(err));
      });
    return () => {
      cancelled = true;
    };
  }, [authedRequest, orderId, attempt]);

  const status = readGiaStatus(gia?.status ?? summary.status);
  const editable = gia !== null && giaIsEditable(status);

  // ONCE, when there is something to land on: the email to fill in when the
  // GIA can still be sent, otherwise the heading.
  useEffect(() => {
    if (!focus || focused.current || (gia === null && loadError === null)) {
      return;
    }
    focused.current = true;
    section.current?.scrollIntoView({ block: "start" });
    const input = editable
      ? section.current?.querySelector<HTMLInputElement>("input")
      : null;
    (input ?? heading.current)?.focus();
  }, [focus, gia, loadError, editable]);

  // A GIA already on file with an unchanged roster: no section at all. The
  // caller gates on the order's summary; this covers the fresher read.
  if (status === "not_required") return null;

  async function send() {
    if (!gia || busy) return;
    const confirmed = gia.signers.map((s) => ({
      ownerId: s.ownerId,
      email: (emails[s.ownerId] ?? "").trim(),
    }));
    const invalid = validateGiaEmails(confirmed);
    setRowErrors(invalid);
    setSummaryError(null);
    if (Object.keys(invalid).length > 0) return;

    setBusy(true);
    const ownerIds = gia.signers.map((s) => s.ownerId);
    try {
      await saveGiaSigners(authedRequest, orderId, confirmed, pushToAccount);
      const read = await sendBondGia(authedRequest, orderId);
      if (readGiaStatus(read.status) !== "sent") {
        // A 2xx that does not say sent is NOT a send. Keep the button live.
        setSummaryError(
          "We couldn’t confirm the GIA was sent. Please try again.",
        );
        return;
      }
      setGia(read);
    } catch (err) {
      const placed = giaServerErrors(err, ownerIds);
      setRowErrors(placed.byOwner);
      setSummaryError(placed.summary);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      ref={section}
      id="section-gia"
      aria-labelledby="gia-heading"
      className="flex w-full flex-col gap-4 rounded-xl border border-brk-border bg-brk-panel p-4 text-left sm:p-6"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <h2
          id="gia-heading"
          ref={heading}
          tabIndex={-1}
          className="text-lg font-semibold text-brk-text outline-none"
        >
          General Indemnity Agreement
        </h2>
        <span
          data-testid="gia-status"
          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${chipClass(status)}`}
        >
          {giaStatusLabel(status)}
        </span>
      </div>

      <p className="text-sm text-brk-dim">
        Every listed owner, and every listed owner’s spouse, signs the General
        Indemnity Agreement before any bond is issued. We email each person a
        link to sign.
      </p>
      {(gia?.requiredReason ?? summary.requiredReason) ===
        "indemnitors_changed" && (
        <p className="text-sm text-brk-dim">
          The owners have changed since the last GIA was signed, so a new one is
          needed.
        </p>
      )}

      {status === "sent" && (gia?.sentAt ?? summary.sentAt) && (
        <p className="text-sm text-brk-text">
          Sent {formatBondDate(gia?.sentAt ?? summary.sentAt ?? "")} — waiting
          for everyone to sign.
        </p>
      )}
      {status === "signed" && (gia?.signedAt ?? summary.signedAt) && (
        <p className="text-sm text-brk-text">
          Signed by everyone{" "}
          {formatBondDate(gia?.signedAt ?? summary.signedAt ?? "")}.
        </p>
      )}

      {loadError !== null && (
        <div className="flex flex-col items-start gap-2">
          <p role="alert" className="text-sm text-brk-danger">
            {loadError}
          </p>
          <button
            type="button"
            onClick={() => setAttempt((n) => n + 1)}
            className="rounded-md border border-brk-border px-3 py-1.5 text-sm font-medium text-brk-text transition-colors hover:bg-brk-bg"
          >
            Try again
          </button>
        </div>
      )}
      {gia === null && loadError === null && (
        <p role="status" className="text-sm text-brk-dim">
          Loading signers…
        </p>
      )}

      {gia !== null && (
        <ul aria-label="Signers" className="flex flex-col gap-3">
          {gia.signers.map((signer) => (
            <li
              key={signer.ownerId}
              data-testid={`gia-signer-${signer.ownerId}`}
              className="flex min-w-0 flex-col gap-2 rounded-lg border border-brk-border p-3"
            >
              <div className="flex min-w-0 flex-col">
                <span className="break-words font-medium text-brk-text">
                  {signer.name}
                </span>
                <span className="text-sm text-brk-dim">
                  {giaBasisLabel(signer)}
                </span>
              </div>
              {editable ? (
                <TextField
                  label={`Email for ${signer.name}`}
                  type="email"
                  autoComplete="off"
                  required
                  value={emails[signer.ownerId] ?? ""}
                  error={rowErrors[signer.ownerId]}
                  onChange={(e) => {
                    const value = e.target.value;
                    setEmails((all) => ({ ...all, [signer.ownerId]: value }));
                    setRowErrors((all) => {
                      const rest = { ...all };
                      delete rest[signer.ownerId];
                      return rest;
                    });
                  }}
                />
              ) : (
                <p className="break-all text-sm text-brk-text">
                  {signer.email ?? "No email on file"}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}

      {editable && (
        <PushToAccountToggle on={pushToAccount} onChange={setPushToAccount} />
      )}

      {editable && (
        <div className="flex flex-col items-start gap-2">
          {summaryError !== null && (
            <p role="alert" className="text-sm text-brk-danger">
              {summaryError}
            </p>
          )}
          <button
            type="button"
            onClick={() => void send()}
            disabled={busy}
            className="w-full rounded-md bg-brk-accent px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-brk-accent-strong disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
          >
            {busy ? "Sending…" : "Send for signature"}
          </button>
        </div>
      )}
    </section>
  );
}
